import { Injectable, OnDestroy } from '@angular/core';
import {
  Firestore,
  CollectionReference,
  DocumentReference,
  collection,
  collectionData,
  doc,
  docData,
  setDoc,
  updateDoc,
} from '@angular/fire/firestore';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { of, switchMap } from 'rxjs';
import { Participant as DolbyParticipant } from '@voxeet/voxeet-web-sdk/types/models/Participant';
import { DeviceDetectorService } from 'ngx-device-detector';
import VoxeetSDK from '@voxeet/voxeet-web-sdk';
import { ModalController } from '@ionic/angular';

import { DolbyService } from '../../services/dolby/dolby.service';
import { GeneralToastComponent } from '../../toasts/general-toast/general-toast.component';
import { SessionsService } from '../sessions/sessions.service';
import { SCSubject } from '../../util/sc-subject.class';
import { UserService } from '../user/user.service';
import { EquipmentService } from '../equipment/equipment.service';
import { StorageService } from '../storage/storage.service';
import { Locations, Session } from '@sc/types';
import { Squadshot, SquadshotCollection, SquadshotReRenderData, SquadshotResult } from '@sc/types';
import { ShareSquadshotPage } from '../../modals/share-squadshot/share-squadshot.page';
import { VideoOffService } from '../video-off/video-off.service';
import { RolesService } from '../roles/roles.service';
import { Roles } from '@sc/types';
import { AnalyticsService } from '../analytics/analytics.service';
import { IdTokenService } from '../id-token/id-token.service';
import { DailyService } from '../daily/daily.service';
import { DailyParticipant } from '@daily-co/daily-js';

@Injectable({
  providedIn: 'root',
})
export class SquadshotService implements OnDestroy {
  squadshot$ = new SCSubject<Squadshot>();
  squadshotResult$ = new SCSubject<SquadshotResult>();
  squadshotRenderReady$ = new SCSubject<boolean>();
  dolbyConversation$ = this.dolbyService.dolbyConversation$;
  dailyCall$ = this.dailyService.dailyCall$;

  sqsCol: CollectionReference = collection(this.firestore, 'squadshots');

  squadshotCountdownTime = 5;

  private gettingReadyToast: ActiveToast<any>;
  private sitOutToast: ActiveToast<any>;
  private loadingSquadshotToast: ActiveToast<any>;

  private countdownInterval: NodeJS.Timer = null;
  private uploadingSelfShot = false;

  constructor(
    private toastrService: ToastrService,
    private sessionsService: SessionsService,
    private firestore: Firestore,
    private dolbyService: DolbyService,
    private dailyService: DailyService,
    private userService: UserService,
    private equipmentService: EquipmentService,
    private storageService: StorageService,
    private deviceDetectorService: DeviceDetectorService,
    private modalController: ModalController,
    private idTokenService: IdTokenService,
    private rolesService: RolesService,
    private analyticsService: AnalyticsService,
    private videoOffService: VideoOffService
  ) {
    this.setupSquadshot();
    this.setupSquadshotResult();

    this.setupConversation();
  }

  setUserParticipating(userParticipating: boolean) {
    const readyCheck = {};
    readyCheck[this.userService.activeUser$.value.uid] = userParticipating;
    this.writeSquadshot({ readyCheck });
  }

  selfParticipating(squadshot = this.squadshot$.value) {
    return squadshot.readyCheck[this.userService.activeUser$.value.uid];
  }

  selfInConference() {
    let result = false;

    if (this.dolbyConversation$.value) {
      this.dolbyConversation$.value?.participants.forEach((participant) => {
        if (
          participant.info.externalId === this.userService.activeUser$.value.uid &&
          participant.status === 'Connected'
        ) {
          result = true;
        }
      });
    } 
    
    if (this.dailyCall$.value) {
      this.dailyService.stageParticipants$.value.forEach((participant) => {
        if (participant.userData.scUID === this.userService.activeUser$.value.uid) {
          result = true;
        }
      });
    }

    return result;
  }

  writeSquadshotResult(result, merge: boolean = true, reRenderData: SquadshotReRenderData = null) {
    if (!reRenderData && !this.squadshot$.value.time) return;
    const sessionID = reRenderData?.sessionID ?? this.sessionsService.studioSession$.value.sessionID;
    const squadshotRef = doc(
      this.sqsCol,
      `${sessionID}/${SquadshotCollection.RESULT}/${reRenderData?.time ?? this.squadshot$.value.time.toString()}`
    );
    return setDoc(squadshotRef, result, { merge });
  }

  takeSquadshot() {
    if (this.rolesService.role$.value < Roles.SHOW_TALENT) {
      this.toastrService.info(`Ask the show talent or the show manager to initiate the SquadShot...`, `SquadShot`, {
        progressBar: true,
        progressAnimation: 'decreasing',
        closeButton: true,
        tapToDismiss: true,
        timeOut: 5 * 1000,
        toastComponent: GeneralToastComponent,
      });
      return;
    }
    if (this.deviceDetectorService.getDeviceInfo().browser === 'Safari') {
      this.toastrService.info(
        `Could not initiate SquadShot using Safari, ask another show talent or show manager in the call to start it!`,
        `SquadShot`,
        {
          progressBar: true,
          progressAnimation: 'decreasing',
          closeButton: true,
          tapToDismiss: true,
          timeOut: 5 * 1000,
          toastComponent: GeneralToastComponent,
        }
      );
      return;
    }

    this.initiateSquadshot();
  }

  /**
   * Returns all squadshots from session
   *
   * @param sessionID - string
   * @returns - Observable<SquadshotResult[]>
   */
  getAllSquadshotsFromSession(sessionID: string) {
    if (!sessionID) {
      console.error("Couldn't get squadshot: No sessionID provided");
      return;
    }

    const squadshotRef = collection(
      this.sqsCol,
      `${sessionID}/${SquadshotCollection.RESULT}`
    ) as CollectionReference<SquadshotResult>;
    return collectionData(squadshotRef, { idField: 'resultID' });
  }

  ngOnDestroy() {
    if (this.dolbyConversation$.value) {
      VoxeetSDK.conference.off('participantUpdated', this.healthCheckDolby.bind(this));
    }

    if (this.dailyCall$.value) {
      this.dailyCall$.value.off('participant-updated', this.healthCheckDaily.bind(this));
    }
  }

  private setupSquadshot(): void {
    this.sessionsService.studioSession$
      .pipe(
        switchMap((session: Session) => {
          if (session) {
            return docData(doc(this.sqsCol, `${session.sessionID}`) as DocumentReference<Squadshot>, {
              idField: 'sessionID',
            });
          } else {
            return of(null);
          }
        })
      )
      .subscribe((squadshot: Squadshot) => {
        if (!squadshot) {
          this.squadshot$.next(null);
          return;
        }
        // Cancel squadshot after page refresh if participant is still participating or the initiatior is the one refreshed.
        // This weird state only happens if you are the only one in the conference and leave during squadshot, or everyone leaves at the same time during squadshot.
        if (
          !this.squadshot$.value &&
          squadshot.readyCheck &&
          squadshot.time &&
          (squadshot.readyCheck[this.userService.activeUser$.value.uid] === true ||
            squadshot.initiator === this.userService.activeUser$.value.uid)
        ) {
          this.endSquadshot();
        }

        if (squadshot.active === false && this.squadshot$.value?.active === true && this.selfInConference()) {
          this.onSquadshotAborted();
        }

        if (squadshot.active === true && this.squadshot$.value?.active !== true && this.selfInConference()) {
          this.onSquadshotInitiated(squadshot);
        }

        if (this.squadshot$.value?.active !== false || squadshot.active !== false) this.squadshot$.next(squadshot);

        if (
          squadshot.active &&
          squadshot.initiator === this.userService.activeUser$.value.uid &&
          this.selfInConference()
        ) {
          this.startCountdown();
        }

        if (
          squadshot.active &&
          squadshot.countdown === 0 &&
          squadshot.readyCheck[this.userService.activeUser$.value.uid] &&
          this.selfInConference()
        ) {
          this.uploadSelfShot();
        }

        if (squadshot.active && squadshot.countdown === 0 && this.selfInConference()) {
          if (this.gettingReadyToast) this.toastrService.clear(this.gettingReadyToast.toastId);
          if (this.sitOutToast) this.toastrService.clear(this.sitOutToast.toastId);

          setTimeout(() => {
            // If no one is participating in the squadshot and the time hits 0, end squadshot. Could happen if everyone has their camera off.
            if (Object.values(squadshot.readyCheck).every((participant) => !participant)) {
              this.endSquadshot();
              return;
            }

            if (this.selfParticipating())
              this.loadingSquadshotToast = this.toastrService.info(
                `Putting SquadShot together! Please wait for the result...`,
                `SquadShot`,
                {
                  closeButton: true,
                  tapToDismiss: true,
                  toastComponent: GeneralToastComponent,
                  disableTimeOut: true,
                }
              ) as ActiveToast<GeneralToastComponent>;
          }, 750);
        }
      });
  }

  private setupSquadshotResult(): void {
    this.squadshot$
      .pipe(
        switchMap((squadshot: Squadshot) => {
          if (squadshot && squadshot.time) {
            return docData(
              doc(
                this.sqsCol,
                `${squadshot.sessionID}/${SquadshotCollection.RESULT}/${squadshot.time.toString()}`
              ) as DocumentReference<SquadshotResult>,
              { idField: 'time' }
            );
          } else {
            return of(null);
          }
        })
      )
      .subscribe((squadshotResult) => {
        this.squadshotResult$.next(squadshotResult);

        if (squadshotResult?.squadshot) {
          this.squadshotSuccessful(squadshotResult.squadshot);
        }
      });
  }

  private onSquadshotInitiated(squadshot) {
    let userParticipating;

    if (this.dolbyConversation$.value) {
      userParticipating =
        !this.videoOffService.videoOff$.value && this.dolbyService.location$.value === Locations.STAGE;
    }

    if (this.dailyCall$.value) {
      userParticipating = !this.videoOffService.videoOff$.value;
    }

    this.setUserParticipating(userParticipating);

    if (!userParticipating || squadshot.initiator === this.userService.activeUser$.value.uid) return;
    this.sitOutToast = this.toastrService.info(
      `SquadShot will be taken in ${this.squadshotCountdownTime} seconds.`,
      `SquadShot`,
      {
        closeButton: false,
        tapToDismiss: false,
        disableTimeOut: true,
        toastComponent: GeneralToastComponent,
      }
    ) as ActiveToast<GeneralToastComponent>;
    this.sitOutToast.toastRef.componentInstance.buttons = [
      {
        label: 'Sit out this one...',
        handler: () => {
          this.setUserParticipating(false);
          this.analyticsService.track('sit out from squadshot');
        },
      },
    ];
  }

  private async onSquadshotAborted() {
    if (this.gettingReadyToast) this.toastrService.clear(this.gettingReadyToast.toastId);
    if (this.sitOutToast) this.toastrService.clear(this.sitOutToast.toastId);

    if (this.selfInConference()) {
      this.toastrService.info(`SquadShot was canceled.`, `SquadShot`, {
        progressBar: true,
        progressAnimation: 'decreasing',
        closeButton: true,
        tapToDismiss: true,
        timeOut: 5 * 1000,
        toastComponent: GeneralToastComponent,
      });
    }
  }

  private startCountdown() {
    if (this.squadshot$.value.countdown !== null || this.countdownInterval) return;
    if (!this.initiatorInConference) {
      this.endSquadshot();
      return;
    }

    let countdown = this.squadshotCountdownTime;
    this.writeSquadshot({ countdown, time: Date.now() });

    this.countdownInterval = setInterval(() => {
      if (this.squadshot$.value.active !== true) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
        return;
      }

      this.writeSquadshot({ countdown });

      if (countdown <= 0) {
        clearInterval(this.countdownInterval);
        return;
      }

      countdown--;
    }, 1000);
  }

  private async uploadSelfShot() {
    // Return if uploading is already in progress, or user already uploaded a shot.
    if (this.uploadingSelfShot || this.squadshotResult$.value?.participants?.[this.userService.activeUser$.value.uid])
      return;

    this.uploadingSelfShot = true;

    if (
      this.videoOffService.videoOff$.value ||
      (this.dolbyConversation$.value && this.dolbyService.location$.value !== Locations.STAGE)
    ) {
      this.setUserParticipating(false);
      this.uploadingSelfShot = false;
      return;
    }

    // To align the timing with the front end for better user experience and flash usage.
    await new Promise((resolve) => setTimeout(resolve, 250));

    try {
      const blob = await this.storageService.captureScreenshotFromMediaStream(
        this.equipmentService.constraints$.value.video
      );

      await this.idTokenService.getFreshIdToken();

      const url = await this.storageService.uploadImage(
        `Sessions/${this.sessionsService.studioSession$.value.sessionID}/squadshots`,
        `${this.userService.activeUser$.value.uid}_${this.squadshot$.value.time}.png`,
        blob
      );

      const participants = {
        [this.userService.activeUser$.value.uid]: url,
      };

      this.writeSquadshotResult({ participants });
    } catch (error) {
      console.error(error);
      this.endSquadshot();
    } finally {
      this.uploadingSelfShot = false;
    }
  }

  private async setupConversation() {
    if (!this.dolbyService.dolbyConversation$.value) {
      await this.dolbyService.dolbyConversation$.toPromise();

      VoxeetSDK.conference.on('participantUpdated', this.healthCheckDolby.bind(this));
    }

    if (this.dailyCall$.value) {
      this.dailyCall$.value.on('participant-updated', this.healthCheckDaily.bind(this));
    }
  }

  private healthCheckDolby(participant: DolbyParticipant) {
    if (!this.squadshot$.value?.active) return;

    if (participant && this.selfInConference() && participant.status !== 'Connected') {
      // CHECK FOR --> INITIATOR LEAVING DURING SQUADSHOT
      if (!this.initiatorInConference()) {
        this.endSquadshot();
        return;
      }

      // CHECK FOR --> READY CHECK ACCEPTED MEMBER LEAVING DURING SQUADSHOT
      const readyCheck = {
        [participant.info.externalId]: false,
      };
      this.writeSquadshot({ readyCheck });
    }
  }

  private healthCheckDaily(participant: DailyParticipant) {
    if (!this.squadshot$.value?.active) return;

    if (participant && this.selfInConference()) {
      // CHECK FOR --> INITIATOR LEAVING DURING SQUADSHOT
      if (!this.initiatorInConference()) {
        this.endSquadshot();
        return;
      }

      console.log('[DAILY HEALTH CHECK]', participant);

      // CHECK FOR --> READY CHECK ACCEPTED MEMBER LEAVING DURING SQUADSHOT
      const readyCheck = {
        // [participant.userData.scUID]: false,
      };
      this.writeSquadshot({ readyCheck });
    }
  }

  private initiateSquadshot() {
    if (this.squadshot$.value?.active && this.initiatorInConference()) {
      if (this.userService.activeUser$.value.uid !== this.squadshot$.value.initiator) {
        this.toastrService.info(`Another SquadShot is in progress...`, `SquadShot`, {
          progressBar: true,
          progressAnimation: 'decreasing',
          closeButton: true,
          tapToDismiss: true,
          timeOut: 5 * 1000,
          toastComponent: GeneralToastComponent,
        });
      }
      return;
    }

    this.gettingReadyToast = this.toastrService.info(`Getting ready to take SquadShot!`, `SquadShot`, {
      closeButton: false,
      tapToDismiss: false,
      toastComponent: GeneralToastComponent,
      disableTimeOut: true,
    }) as ActiveToast<GeneralToastComponent>;
    this.gettingReadyToast.toastRef.componentInstance.buttons = [
      {
        label: 'Cancel',
        handler: () => {
          this.endSquadshot();
        },
      },
    ];

    const readyCheck = {};

    if (this.dolbyConversation$.value) {
      this.dolbyConversation$.value.participants.forEach((participant) => {
        if (participant.status !== 'Connected') return;
        if (participant.info.externalId === this.userService.activeUser$.value.uid) {
          // Initiator is ready automatically
          readyCheck[participant.info.externalId] = true;
        } else {
          readyCheck[participant.info.externalId] = null;
        }
      });
    }

    if (this.dailyCall$.value) {
      this.dailyService.stageParticipants$.value.forEach((participant) => {
        if (participant.userData.scUID === this.userService.activeUser$.value.uid) {
          // Initiator is ready automatically
          readyCheck[participant.userData.scUID] = true;
        } else {
          readyCheck[participant.userData.scUID] = null;
        }
      });
    }

    this.writeSquadshot(
      { active: true, initiator: this.userService.activeUser$.value.uid, readyCheck, countdown: null, time: null },
      false
    );
  }

  private endSquadshot(success = false) {
    if (this.loadingSquadshotToast) this.toastrService.clear(this.loadingSquadshotToast.toastId);
    if (this.sitOutToast) this.toastrService.clear(this.sitOutToast.toastId);

    const sqShot: Partial<Squadshot> = {
      active: success === false ? false : null,
      initiator: null,
      readyCheck: {},
      countdown: null,
      time: null,
    };

    if (success === true) {
      sqShot.lastSquadshot = this.squadshot$.value.time;
    }

    this.writeSquadshot(sqShot, false);
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private initiatorInConference() {
    let result = false;

    if (this.dolbyConversation$.value) {
      this.dolbyConversation$.value.participants.forEach((participant) => {
        if (participant.info.externalId === this.squadshot$.value.initiator && participant.status === 'Connected') {
          result = true;
        }
      });
    }
    
    if (this.dailyCall$.value) {
      this.dailyService.stageParticipants$.value.forEach((participant) => {
        if (participant.userData.scUID === this.squadshot$.value.initiator) {
          result = true;
        }
      });
    }

    return result;
  }

  private writeSquadshot(squadshot, merge: boolean = true) {
    const sessionID = this.sessionsService.studioSession$.value.sessionID;
    const docRef = doc(this.sqsCol, sessionID);
    setDoc(docRef, squadshot, { merge: true });

    if (!merge) updateDoc(docRef, { readyCheck: squadshot.readyCheck, countdown: squadshot.countdown });
  }

  private squadshotSuccessful(url) {
    if (this.selfInConference() && this.selfParticipating()) {
      this.showSquadshotModal(url);
      this.endSquadshot(true);
    }
  }

  private showSquadshotModal(url) {
    this.modalController
      .create({
        component: ShareSquadshotPage,
        componentProps: {
          url,
        },
        showBackdrop: true,
        backdropDismiss: true,
        animated: true,
        cssClass: 'share-squadshot-modal',
      })
      .then((modal) => {
        modal.present();
      });
  }
}
